'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { aiAssistantApi, type PromptTemplate, type ConversationCategory } from '@/lib/api/ai-assistant';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';
import { PageState } from '@/components/common/feedback/PageState';

const CATEGORIES: ConversationCategory[] = ['GENERAL', 'IT', 'HR', 'ADMIN', 'FINANCE'];

/**
 * Prompt 模板管理页面
 */
export default function PromptsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [prompts, setPrompts] = useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<PromptTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'GENERAL' as ConversationCategory,
    content: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    setIsLoading(true);
    try {
      const response: any = await aiAssistantApi.getPromptTemplates();
      setPrompts(response.data.items || []);
    } catch (err) {
      console.error('Failed to load prompts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPrompt(null);
    setFormData({ name: '', category: 'GENERAL', content: '' });
    setShowForm(true);
  };

  const handleEdit = (prompt: PromptTemplate) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      category: prompt.category,
      content: prompt.content,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.content.trim()) return;

    setIsSaving(true);
    try {
      if (editingPrompt) {
        await aiAssistantApi.updatePromptTemplate(editingPrompt.id, {
          name: formData.name,
          content: formData.content,
        });
      } else {
        await aiAssistantApi.createPromptTemplate(formData);
      }
      setShowForm(false);
      loadPrompts();
    } catch (err) {
      console.error('Failed to save prompt:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t.common.confirmDelete || 'Are you sure?',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      // Note: Delete API not implemented in backend yet
      setPrompts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Failed to delete prompt:', err);
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await aiAssistantApi.activatePromptTemplate(id);
      loadPrompts();
    } catch (err) {
      console.error('Failed to activate prompt:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t.aiAssistant.prompts.title}
          </h1>
          <p className="text-gray-500 mt-1">
            {t.aiAssistant.prompts.description}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <Plus className="w-4 h-4" />
          {t.aiAssistant.prompts.create}
        </button>
      </div>

      {/* 模板列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {prompts.length === 0 ? (
          <div className="py-12">
            <PageState
              variant="empty"
              title={t.aiAssistant.prompts.emptyTitle}
              description={t.aiAssistant.prompts.emptyDesc}
              layout="center"
              className="mx-auto w-full max-w-2xl border-0 bg-transparent"
            />
          </div>
        ) : (
          <div className="divide-y">
            {prompts.map((prompt) => (
              <div key={prompt.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-gray-900">{prompt.name}</h3>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                        {t.aiAssistant?.categories?.[prompt.category as keyof typeof t.aiAssistant.categories] || prompt.category}
                      </span>
                      {prompt.isActive && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-600">
                          {t.aiAssistant.prompts.active}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        v{prompt.version}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                      {prompt.content}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {!prompt.isActive && (
                      <button
                        onClick={() => handleActivate(prompt.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        title={t.aiAssistant.prompts.activateAction}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(prompt)}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      title={t.aiAssistant.prompts.editAction}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(prompt.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      title={t.aiAssistant.prompts.deleteAction}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 编辑/创建表单弹窗 */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowForm(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl bg-white rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingPrompt
                  ? t.aiAssistant.prompts.dialogEditTitle
                  : t.aiAssistant.prompts.dialogCreateTitle}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.aiAssistant.prompts.name}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  placeholder={t.aiAssistant.prompts.namePlaceholder}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.aiAssistant.prompts.category}
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as ConversationCategory }))}
                  disabled={!!editingPrompt}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 disabled:bg-gray-100"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {t.aiAssistant?.categories?.[cat as keyof typeof t.aiAssistant.categories] || cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.aiAssistant.prompts.content}
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  rows={10}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 font-mono text-sm"
                  placeholder={t.aiAssistant.prompts.contentPlaceholder}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-100"
              >
                {t.aiAssistant.prompts.cancel}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !formData.name.trim() || !formData.content.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {t.aiAssistant.prompts.save}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
